<template>
  <div class="order-tools-page">
    <div class="search-bar">
      <span class="search-label">用户订单查询</span>
      <a-input
        v-model:value="userId"
        placeholder="请输入用户 ID"
        style="width: 260px;"
        @press-enter="fetchOrders"
        allow-clear
      />
      <a-button type="primary" :loading="loading" @click="fetchOrders" shape="circle">
        <template #icon><SearchOutlined /></template>
      </a-button>
      <a-button danger :disabled="!canDelete" :loading="deleteLoading" @click="confirmDelete">
        一键删除所有订单
      </a-button>
      <span v-if="searched && total > 0" class="result-count">共 {{ total }} 条订单</span>
    </div>

    <div v-if="searched && total > 0 && userInfo.user_id" class="user-info-bar">
      <span class="user-info-item"><span class="info-label">用户 ID</span>{{ userInfo.user_id }}</span>
      <a-divider type="vertical" />
      <span class="user-info-item"><span class="info-label">昵称</span>{{ userInfo.nickname || '—' }}</span>
    </div>

    <a-alert :message="noticeMessage" type="info" show-icon style="text-align: left;" v-if="noticeMessage" />

    <a-empty v-if="searched && !loading && total === 0" description="该用户暂无订单" style="margin-top: 80px;" />

    <a-table
      v-if="searched"
      v-show="total > 0"
      class="ant-table-striped order-table"
      :row-key="record => record.id"
      :data-source="dataSource"
      :columns="columns"
      :pagination="pagination"
      :loading="loading"
      @change="handleTableChange"
      :row-class-name="(_record, index) => (index % 2 === 1 ? 'table-striped' : null)"
    >
      <template #is_renew="{ record }">
        <a-tag v-if="record.is_renew" color="green">续费</a-tag>
        <a-tag v-else>新购</a-tag>
      </template>
      <template #created_at="{ record }">
        <div>创建时间: {{ record.created_at.toLocaleString() }}</div>
        <div>修改时间: {{ record.updated_at.toLocaleString() }}</div>
      </template>
    </a-table>

    <a-modal
      v-model:open="deleteModalOpen"
      title="删除用户订单"
      ok-text="确认删除"
      cancel-text="取消"
      ok-type="danger"
      :confirm-loading="deleteLoading"
      @ok="doDelete"
    >
      <a-alert
        message="只支持删除测试账号的订单数据"
        type="warning"
        show-icon
        style="margin-bottom: 12px;"
      />
      <p>确认删除用户 <strong>{{ userInfo.user_id }}</strong>（{{ userInfo.nickname || '—' }}）下的所有订单？</p>
    </a-modal>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { SearchOutlined } from '@ant-design/icons-vue'
import request from '@/utils/request'

export default defineComponent({
  components: { SearchOutlined },
  data() {
    return {
      userId: '',
      loading: false,
      searched: false,
      dataSource: [],
      total: 0,
      noticeMessage: '',
      userInfo: { user_id: '', nickname: '' },
      deleteLoading: false,
      deleteModalOpen: false,
      pagination: {
        total: 0,
        current: 1,
        pageSize: 20,
        'show-size-changer': true,
        pageSizeOptions: ['20', '50', '100'],
      },
      columns: [
        { title: '订单 ID', dataIndex: 'order_id', key: 'order_id' },
        { title: '用户 ID', dataIndex: 'user_id', key: 'user_id' },
        { title: '用户昵称', key: 'nickname', dataIndex: 'nickname' },
        { title: '商品 ID', dataIndex: 'vip_meal_id', key: 'vip_meal_id' },
        { title: '商品名称', key: 'vip_meal_name', dataIndex: 'vip_meal_name' },
        { title: '支付金额', dataIndex: 'pay_amount', key: 'pay_amount' },
        { title: '货币', dataIndex: 'currency', key: 'currency' },
        { title: '支付时间', dataIndex: 'pay_time', key: 'pay_time' },
        { title: '支付状态', dataIndex: 'trade_status', key: 'trade_status' },
        { title: '交易 ID', dataIndex: 'transaction_id', key: 'transaction_id' },
        { title: '原始交易 ID', dataIndex: 'origin_transaction_id', key: 'origin_transaction_id' },
        { title: '支付渠道', dataIndex: 'payment_source', key: 'payment_source' },
        { title: '是否续费', dataIndex: 'is_renew', key: 'is_renew', slots: { customRender: 'is_renew' } },
        { title: '备注', dataIndex: 'mark', key: 'mark' },
        { title: '创建时间', dataIndex: 'created_at', key: 'created_at', slots: { customRender: 'created_at' } },
      ],
    }
  },
  computed: {
    canDelete() {
      return this.searched && this.total > 0
    },
  },
  methods: {
    async fetchOrders() {
      if (!this.userId.trim()) return
      this.pagination.current = 1
      this.userInfo = { user_id: '', nickname: '' }
      await this.loadOrders()
    },
    async handleTableChange(pagi = { pageSize: 20, current: 1 }) {
      this.pagination.current = pagi.current
      this.pagination.pageSize = pagi.pageSize
      await this.loadOrders()
    },
    async loadOrders() {
      this.loading = true
      this.searched = true
      try {
        const rsp = await request.get('/api/orders/search', {
          user_id: this.userId.trim(),
          offset: (this.pagination.current - 1) * this.pagination.pageSize,
          limit: this.pagination.pageSize,
        })
        for (const item of rsp.data.list) {
          item.created_at = new Date(item.created_at)
          item.updated_at = new Date(item.updated_at)
        }
        this.dataSource = rsp.data.list
        this.total = rsp.data.pagination.total
        this.pagination.total = this.total
        this.noticeMessage = `共 ${this.total} 条`
        if (rsp.data.list.length > 0) {
          this.userInfo = { user_id: rsp.data.list[0].user_id, nickname: rsp.data.list[0].nickname }
        }
      } finally {
        this.loading = false
      }
    },
    confirmDelete() {
      if (!this.canDelete) return
      this.deleteModalOpen = true
    },
    async doDelete() {
      this.deleteLoading = true
      try {
        await request.delete('/api/orders/delete_user_order', { user_id: this.userInfo.user_id })
        this.deleteModalOpen = false
        this.dataSource = []
        this.total = 0
        this.noticeMessage = ''
        this.userInfo = { user_id: '', nickname: '' }
        this.searched = false
      } finally {
        this.deleteLoading = false
      }
    },
  },
})
</script>

<style scoped>
.order-tools-page {
  padding: 24px;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.search-label {
  font-size: 15px;
  font-weight: 600;
  color: #222;
  white-space: nowrap;
}

.result-count {
  font-size: 13px;
  color: #888;
}

.user-info-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  margin-bottom: 16px;
  background: #f6f8fa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  font-size: 13px;
}

.user-info-item {
  color: #333;
}

.info-label {
  color: #999;
  margin-right: 6px;
}

.order-table {
  margin-top: 12px;
}

.ant-table-striped :deep(.table-striped) td {
  background-color: #fafafa;
}
</style>
